import React, { useContext } from 'react';
import { CameraResultType, Plugins } from '@capacitor/core';
import { IconButton, TextField } from 'office-ui-fabric-react';

import { Styled } from './BCResult.styles';
import { Props, TableItem } from './BCResult.types';

import { context } from './context';
import { CheckVehicles } from '../../ControllerTask/BCResult/BCResult.types';
import { immutableSplice } from 'utils/array';
import {
  securePostRequest,
  stringifySearchParams,
} from '../../../../utils/request';
import { useBcResultUtilities } from '../../../../hooks/useBcResultUtilities';

const { Camera } = Plugins;

const vehicleInfo: TableItem[] = [
  {
    name: 'route_short_name',
    title: 'Номер маршрута',
    dropdown: true,
  },
  {
    name: 'depot_number',
    title: 'Гаражный номер ТС',
    dropdown: true,
  },
  {
    name: 'table_number',
    title: 'Табельный номер водителя',
    number: true,
  },
];

const violations: TableItem[] = [
  {
    name: 'kickout',
    number: true,
    title: 'Высажено безбилетных',
  },
  {
    name: 'OVDdeliver',
    number: true,
    title: 'Доставлено в ОВД',
  },
];

const takeTickets = ['СК', 'ВЕСБ', 'Прочие легитимные', 'Нелегитимные'];

export const EditVehicle: React.FC<Props> = () => {
  const {
    resultTask,
    editingVehicleIndex,
    setResultTask,
    tickets,
    routes,
    vehicles,
  } = useContext(context);

  const {
    parseValue,
    getDropdown,
    ...bcResultUtilities
  } = useBcResultUtilities(setResultTask, vehicles, routes);

  if (editingVehicleIndex === null || !resultTask) {
    return null;
  }

  const vehicle = resultTask.check_vehicles[editingVehicleIndex];

  const onChange = (
    field: keyof CheckVehicles,
    value: string,
    convertToNumber?: boolean
  ) =>
    setResultTask((prev) => {
      if (!prev) return null;
      const prevItem = prev.check_vehicles[editingVehicleIndex];
      const updatedList = immutableSplice<any>(
        prev.check_vehicles,
        editingVehicleIndex,
        1,
        {
          ...prevItem,
          [field]: convertToNumber ? Number(value) : value,
        }
      );
      return {
        ...prev,
        check_vehicles: updatedList,
      };
    });

  const getField = (name: keyof CheckVehicles, number?: true) => (
    <TextField
      type={number && 'number'}
      value={vehicle[name] as string}
      onChange={(_, value) => onChange(name, value || '', number)}
    />
  );

  const handleClickCamera = async () => {
    const image = await Camera?.getPhoto({
      quality: 100,
      allowEditing: true,
      resultType: CameraResultType.Uri,
    });
    if (image.webPath) {
      const name = Date.now() + '.jpg';

      const blob = await fetch(image.webPath).then((res) => res.blob());

      const file = new File([blob], name, { type: blob.type });

      const formData = new FormData();
      formData.append('file', file);

      await securePostRequest(
        '/api/v1/pass/controllertask/photo?' +
          stringifySearchParams({
            controller_task_id: resultTask.id,
            title: name,
          }),
        undefined,
        {
          type: 'file',
        }
      )(formData);
    }
  };

  const onChangeTicketItem = (
    sellTickets: 'sell_tickets' | 'take_tickets',
    relativeIndex: number,
    number: number
  ) => {
    bcResultUtilities.onChangeTicketItem(
      sellTickets,
      relativeIndex,
      number,
      editingVehicleIndex
    );
  };

  return (
    <>
      <DetailsTable title="">
        {vehicleInfo.map(({ name, title, number, dropdown }) => (
          <Styled.InputWrapper key={name} withBorder>
            <td>{title}</td>
            <td>
              {dropdown
                ? getDropdown(
                    name as 'depot_number' | 'route_short_name',
                    vehicle,
                    editingVehicleIndex
                  )
                : getField(name, number)}
            </td>
          </Styled.InputWrapper>
        ))}
      </DetailsTable>
      <DetailsTable title="Продано билетов">
        {tickets.map(({ id, name }) => {
          const relativeIndex = vehicle.sell_tickets.findIndex(
            ({ ticket_id }) => ticket_id === id
          );
          const relativeItem = vehicle.sell_tickets[relativeIndex];
          return (
            <tr key={id}>
              <td>{name}</td>
              <td>
                <TextField
                  type="number"
                  value={`${relativeItem?.count || 0}`}
                  onChange={(_, value) =>
                    onChangeTicketItem(
                      'sell_tickets',
                      relativeIndex,
                      parseValue(value, true)
                    )
                  }
                />
              </td>
            </tr>
          );
        })}
      </DetailsTable>
      <DetailsTable title="Нарушение">
        {violations.map(({ title, number, name }) => (
          <tr key={name}>
            <td>{title}</td>
            <td>{getField(name, number)}</td>
          </tr>
        ))}
      </DetailsTable>
      <DetailsTable title="Изъято">
        {vehicle.take_tickets.map(({ ticket_id, count }, index) => (
          <tr key={ticket_id}>
            <td>{takeTickets[index]}</td>
            <td>
              <TextField
                type="number"
                value={`${count}`}
                onChange={(_, value) =>
                  onChangeTicketItem(
                    'take_tickets',
                    index,
                    parseValue(value, true)
                  )
                }
              />
            </td>
          </tr>
        ))}
      </DetailsTable>

      <table>
        <Styled.InputWrapper withBorder>
          <td>Рапорт</td>
          <td>{getField('driver_raport', true)}</td>
          <td>
            <IconButton
              onClick={handleClickCamera}
              iconProps={{ iconName: 'Camera' }}
            />
          </td>
        </Styled.InputWrapper>
      </table>
    </>
  );
};

const DetailsTable: React.FC<{ title: string }> = ({ title, children }) => {
  return (
    <Styled.DetailsTable>
      {title && (
        <thead>
          <tr>
            <th colSpan={2}>{title}</th>
          </tr>
        </thead>
      )}
      <tbody>{children}</tbody>
    </Styled.DetailsTable>
  );
};
